import { typesofUsers } from "../../../config";

export const BANK_INCOME_DEPOSIT_LINKING_FIELD = "BankIncomeDepositLinking";
export const PAYROLL_LINKING_FIELD = "PayrollLinking";
export const INCOME_BANKING_TAB_LABEL = "Income & Banking";

export type LinkingSectionMode = "Hide" | "Optional" | "Required";

export const toYesNoLinkingFlag = (value: unknown): "Yes" | "No" =>
  value == 1 ||
  value == 2 ||
  value === true ||
  value === "Yes" ||
  value === "Required" ||
  value === "Optional"
    ? "Yes"
    : "No";

export const toApiLinkingFlag = (value: unknown): 0 | 1 | 2 => {
  if (value === "Optional" || value == 2) {
    return 2;
  }
  if (
    value === "Yes" ||
    value === "Required" ||
    value == 1 ||
    value === true
  ) {
    return 1;
  }
  return 0;
};

export const toApiYesNoLinkingFlag = (
  formValue: unknown,
  existingValue?: unknown
): 0 | 1 | 2 => {
  if (toYesNoLinkingFlag(formValue) === "No") {
    return 0;
  }
  if (existingValue === "Optional" || existingValue == 2) {
    return 2;
  }
  return 1;
};

export const toLinkingSectionMode = (
  value: unknown
): LinkingSectionMode | null => {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  return toStandardsLinkingFlag(value);
};

export const isLinkingSettingEnabled = (value: unknown): boolean =>
  toLinkingSectionMode(value) !== "Hide";

export const applyLinkingSectionMode = (
  profileSectionState: unknown,
  linkingMode: LinkingSectionMode | null
): { visible: boolean; required: boolean; state: number } => {
  if (linkingMode === "Hide") {
    return { visible: false, required: false, state: 3 };
  }
  if (linkingMode === "Optional") {
    return { visible: true, required: false, state: 2 };
  }
  if (linkingMode === "Required") {
    return { visible: true, required: true, state: 1 };
  }
  const state = Number(profileSectionState);
  return {
    visible: state == 1 || state == 2,
    required: state == 1,
    state: Number.isFinite(state) ? state : 3,
  };
};

// Linking Hide/Optional/Required controls the linking widget only.
// Document collection stays on the existing profile section state, so a
// migrated or disabled linking flag cannot hide a required upload.
export const applyDocumentSectionVisibility = (
  profileSectionState: unknown,
  _linkingMode: LinkingSectionMode | null
): { visible: boolean; required: boolean; state: number } =>
  applyLinkingSectionMode(profileSectionState, null);

const APPLICATION_PROFILE_ID_KEYS = [
  "ApplicationProfileID",
  "applicationProfileID",
  "application_profile_id",
  "ApplicationProfileId",
  "application_profile",
  "ProfileID",
  "profileId",
] as const;

const unwrapProfileId = (value: unknown): string | number | null => {
  if (value == null || value === "") {
    return null;
  }
  if (typeof value === "object") {
    const nested = value as Record<string, any>;
    return unwrapProfileId(nested.value ?? nested.ID ?? nested.id);
  }
  return value as string | number;
};

export const resolveApplicationProfileId = (
  ...sources: Array<Record<string, any> | null | undefined>
): string | number | null => {
  for (const source of sources) {
    if (!source) {
      continue;
    }
    for (const key of APPLICATION_PROFILE_ID_KEYS) {
      const id = unwrapProfileId(source[key]);
      if (id != null) {
        return id;
      }
    }
    for (const key of Object.keys(source)) {
      if (key.toLowerCase() === "applicationprofileid") {
        const id = unwrapProfileId(source[key]);
        if (id != null) {
          return id;
        }
      }
    }
    if (source.data && typeof source.data === "object") {
      const nestedId = resolveApplicationProfileId(source.data);
      if (nestedId != null) {
        return nestedId;
      }
    }
  }
  return null;
};

const toRecordList = (payload: unknown): Array<Record<string, any>> => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && typeof payload === "object") {
    const nested = payload as Record<string, any>;
    if (Array.isArray(nested.profiledetails)) {
      return nested.profiledetails;
    }
    if (Array.isArray(nested.data)) {
      return nested.data;
    }
    if (Array.isArray(nested.records)) {
      return nested.records;
    }
  }
  return [];
};

export const normalizeApplicationProfileRequestId = (
  value: unknown
): string | number | null => {
  if (Array.isArray(value)) {
    return unwrapProfileId(value[0]);
  }
  return unwrapProfileId(value);
};

const recordProfileId = (row: Record<string, any>): string | number | null =>
  unwrapProfileId(row.ID ?? row.id ?? row.ApplicationProfileID);

export const selectApplicationProfileRecord = (
  details: unknown,
  profileId: unknown,
  options?: { allowUnmatchedSingleton?: boolean }
): Record<string, any> | null => {
  const requested = normalizeApplicationProfileRequestId(profileId);
  const list = toRecordList(details);
  if (!list.length) {
    return null;
  }
  if (requested == null) {
    return list.length === 1 ? list[0] : null;
  }
  const matched = list.filter((row) => {
    const rowId = recordProfileId(row);
    return rowId != null && String(rowId) === String(requested);
  });
  if (matched.length === 1) {
    return matched[0];
  }
  if (options?.allowUnmatchedSingleton && list.length === 1) {
    return list[0];
  }
  return null;
};

const normalizeName = (value: unknown): string =>
  String(value || "").trim().toLowerCase();

const propertyIdentity = (property: Record<string, any>): Array<string | number> =>
  [property.id, property.ID, property.PropertyID, property.property_id, property.OfficeID].filter(
    (value) => value != null && value !== ""
  );

export const resolveApplicationProfileIdFromProperties = (
  properties: unknown,
  propertyName: unknown,
  propertyId?: unknown
): string | number | null => {
  const list = toRecordList(properties);
  if (!list.length) {
    return null;
  }
  const requestedId =
    propertyId == null || propertyId === "" ? null : String(propertyId);
  if (requestedId) {
    const matchedById = list.filter((property) =>
      propertyIdentity(property).some((value) => String(value) === requestedId)
    );
    if (matchedById.length === 1) {
      return resolveApplicationProfileId(matchedById[0]);
    }
  }
  const name = normalizeName(propertyName);
  if (!name) {
    return null;
  }
  const matchedByName = list.filter((property) =>
    [
      property.PropertyName,
      property.OfficeName,
      property.office_property_name,
      property.property_name,
      property.Name,
      property.name,
    ].some((candidate) => normalizeName(candidate) === name)
  );
  if (matchedByName.length !== 1) {
    return null;
  }
  return resolveApplicationProfileId(matchedByName[0]);
};

export const resolveSoleApplicationProfileId = (profiles: unknown): string | number | null => {
  const list = toRecordList(profiles);
  if (list.length !== 1) {
    return null;
  }
  return list[0]?.ID ?? list[0]?.id ?? null;
};

export const toStandardsLinkingFlag = (
  value: unknown
): "Hide" | "Optional" | "Required" => {
  if (value === undefined || value === null || value === "") {
    return "Required";
  }
  if (value === "Optional" || value == 2) {
    return "Optional";
  }
  if (value == 1 || value === true || value === "Yes" || value === "Required") {
    return "Required";
  }
  return "Hide";
};

export const toLoadedYesNoLinkingFlag = (value: unknown): "Yes" | "No" =>
  value === undefined || value === null || value === ""
    ? "Yes"
    : toYesNoLinkingFlag(value);

export const canModifyApplicationProfileLinking = (
  permissions: unknown,
  userRole?: string
): boolean => {
  if (userRole === typesofUsers.docuverusSuperAdmin) {
    return true;
  }
  if (!permissions || typeof permissions !== "object") {
    return false;
  }
  const moduleData = permissions as Record<string, any>;
  const parentMenus = Array.isArray(moduleData.ParentMenus)
    ? moduleData.ParentMenus
    : [];
  const settingsParent = parentMenus.find(
    (item) => item?.MenuName === "Settings"
  );
  if (!settingsParent || settingsParent.Access !== true) {
    return false;
  }
  const settingsMenus = Array.isArray(moduleData.Settings)
    ? moduleData.Settings
    : [];
  const profileMenu = settingsMenus.find(
    (item) => item?.MenuName === "Application Profile"
  );
  return profileMenu?.Access === true;
};

export const isIncomeBankingProfileTab = (currentProfileTab?: string | null): boolean => {
  if (!currentProfileTab) {
    return false;
  }
  try {
    return decodeURIComponent(currentProfileTab) === INCOME_BANKING_TAB_LABEL;
  } catch {
    return currentProfileTab === INCOME_BANKING_TAB_LABEL;
  }
};

const toLeadSourceIds = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.map((item) =>
      item && typeof item === "object" && "value" in item ? (item as { value: unknown }).value : item
    );
  }
  if (typeof value === "string" && value.length) {
    return value.split(",").filter(Boolean);
  }
  return value;
};

export const ASSOCIATED_PROPERTY_IDS_FIELD = "AssociatedPropertyIDs";

export const readSavedLinkingFlags = (
  profile: Record<string, any> | null | undefined
): Record<string, 0 | 1 | 2> | null => {
  if (!profile) {
    return null;
  }
  const flags: Record<string, 0 | 1 | 2> = {};
  if (
    profile[BANK_INCOME_DEPOSIT_LINKING_FIELD] !== undefined &&
    profile[BANK_INCOME_DEPOSIT_LINKING_FIELD] !== null &&
    profile[BANK_INCOME_DEPOSIT_LINKING_FIELD] !== ""
  ) {
    flags[BANK_INCOME_DEPOSIT_LINKING_FIELD] = toApiLinkingFlag(
      profile[BANK_INCOME_DEPOSIT_LINKING_FIELD]
    );
  }
  if (
    profile[PAYROLL_LINKING_FIELD] !== undefined &&
    profile[PAYROLL_LINKING_FIELD] !== null &&
    profile[PAYROLL_LINKING_FIELD] !== ""
  ) {
    flags[PAYROLL_LINKING_FIELD] = toApiLinkingFlag(profile[PAYROLL_LINKING_FIELD]);
  }
  return Object.keys(flags).length ? flags : null;
};

export const propertyMatchesApplicationProfile = (
  property: Record<string, any> | null | undefined,
  profileId: unknown
): boolean => {
  const requested = normalizeApplicationProfileRequestId(profileId);
  if (!property || requested == null) {
    return false;
  }
  const propertyProfileId = unwrapProfileId(
    property.ApplicationProfileID ??
      property.applicationProfileID ??
      property.application_profile ??
      property.application_profile_id
  );
  return propertyProfileId != null && String(propertyProfileId) === String(requested);
};

export const listAssociatedPropertyIds = (
  properties: unknown,
  profileId: unknown
): Array<string | number> =>
  toRecordList(properties)
    .filter((property) => propertyMatchesApplicationProfile(property, profileId))
    .map((property) => property.id ?? property.ID ?? property.PropertyID ?? property.property_id)
    .filter((value) => value != null && value !== "");

export const appendProfileLinkingFlagsToFormData = (
  formData: FormData,
  profile: Record<string, any> | null | undefined
): FormData => {
  const flags = readSavedLinkingFlags(profile);
  if (!flags) {
    return formData;
  }
  Object.entries(flags).forEach(([key, value]) => {
    formData.append(key, String(value));
  });
  return formData;
};

export const resolveSelectedApplicationProfile = (
  details: unknown,
  profiles: unknown,
  profileId: unknown
): Record<string, any> | null =>
  selectApplicationProfileRecord(details, profileId) ??
  toRecordList(profiles).find((profile) => {
    const rowId = recordProfileId(profile);
    const requested = normalizeApplicationProfileRequestId(profileId);
    return requested != null && rowId != null && String(rowId) === String(requested);
  }) ??
  null;

export const buildApplicationProfileLinkingSaveData = (
  profile: Record<string, any> | null | undefined,
  linkingValues: Record<string, unknown>,
  profileId?: unknown,
  options?: { associatedPropertyIds?: Array<string | number>; linkingOnly?: boolean }
): Record<string, any> | null => {
  if (!profile) {
    return null;
  }
  const requestedId = normalizeApplicationProfileRequestId(profileId);
  const loadedId = recordProfileId(profile);
  const id = requestedId ?? loadedId;
  if (id == null) {
    return null;
  }

  const linkingFields = {
    [BANK_INCOME_DEPOSIT_LINKING_FIELD]: toApiLinkingFlag(
      linkingValues[BANK_INCOME_DEPOSIT_LINKING_FIELD]
    ),
    [PAYROLL_LINKING_FIELD]: toApiLinkingFlag(
      linkingValues[PAYROLL_LINKING_FIELD]
    ),
  };
  const associatedPropertyIds = (options?.associatedPropertyIds || []).filter(
    (value) => value != null && value !== ""
  );
  const propertyPropagation =
    associatedPropertyIds.length > 0
      ? { [ASSOCIATED_PROPERTY_IDS_FIELD]: associatedPropertyIds }
      : {};

  const sameProfile = loadedId != null && String(loadedId) === String(id);
  if (options?.linkingOnly || !sameProfile) {
    const subscriberId = profile.SubscriberID ?? profile.SubscriberId;
    return {
      ID: sameProfile ? loadedId : id,
      ...(subscriberId != null && subscriberId !== ""
        ? { SubscriberID: subscriberId }
        : {}),
      ...linkingFields,
      ...propertyPropagation,
    };
  }

  const {
    CopyFromApplicationProfileID: _copyFrom,
    profiledetails: _profileDetails,
    ...profileFields
  } = profile;

  return {
    ...profileFields,
    ID: loadedId ?? id,
    CopyProfileID: 2,
    LeadSourceIDs: toLeadSourceIds(profile.LeadSourceIDs),
    ...linkingFields,
    ...propertyPropagation,
  };
};
